import React, { useState } from 'react';
import { integrityAPI } from '../../api';
import { cn, formatDateTime } from '../../utils';
import { Card, Button, Spinner } from '../../components/ui';
import { Shield, RefreshCw, CheckCircle, XCircle } from 'lucide-react';

export const IntegrityPage = () => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const verifyIntegrity = async () => {
    setLoading(true);
    try {
      const response = await integrityAPI.verify();
      setResult(response.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to verify integrity');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="integrity-page">
      <div>
        <h1 className="text-2xl font-bold">Ledger Integrity</h1>
        <p className="text-gray-500">Verify the append-only ledger hash chain</p>
      </div>

      <Card className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 bg-primary-100 rounded-lg">
            <Shield className="w-8 h-8 text-primary-600" />
          </div>
          <div>
            <h3 className="font-semibold">Hash Chain Verification</h3>
            <p className="text-sm text-gray-500">
              Verify that the event ledger has not been tampered with
            </p>
          </div>
        </div>

        <Button onClick={verifyIntegrity} disabled={loading} className="mb-6">
          {loading ? <Spinner size="sm" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Run Verification
        </Button>

        {result && (
          <div className={cn(
            'p-4 rounded-lg',
            result.is_valid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          )}>
            <div className="flex items-center gap-3 mb-4">
              {result.is_valid ? (
                <CheckCircle className="w-6 h-6 text-green-600" />
              ) : (
                <XCircle className="w-6 h-6 text-red-600" />
              )}
              <span className={cn('font-semibold', result.is_valid ? 'text-green-800' : 'text-red-800')}>
                {result.message}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Total Entries</p>
                <p className="font-semibold">{result.total_entries}</p>
              </div>
              <div>
                <p className="text-gray-500">Verified</p>
                <p className="font-semibold">{result.verified_entries}</p>
              </div>
              <div>
                <p className="text-gray-500">Last Verified</p>
                <p className="font-semibold">{formatDateTime(result.last_verified_at)}</p>
              </div>
              {result.first_mismatch_id && (
                <div>
                  <p className="text-gray-500">First Mismatch</p>
                  <p className="font-semibold font-mono text-xs">{result.first_mismatch_id.slice(0, 8)}...</p>
                </div>
              )}
            </div>
          </div>
        )}
      </Card>

      <Card className="p-6">
        <h3 className="font-semibold mb-4">How It Works</h3>
        <div className="space-y-4 text-sm text-gray-600">
          <p>
            <strong>1. Append-Only:</strong> The event ledger only allows INSERT operations. 
            UPDATE and DELETE are blocked at the application level.
          </p>
          <p>
            <strong>2. Hash Chain:</strong> Each entry contains a hash computed from the previous 
            entry's hash and its own data, creating an unbreakable chain.
          </p>
          <p>
            <strong>3. Verification:</strong> Walking through the chain and recomputing hashes 
            ensures no entry has been modified or inserted maliciously.
          </p>
          <p>
            <strong>4. Corrections:</strong> Any adjustments are made via new ledger entries 
            (ADMIN_ADJUSTMENT, REVERSAL), never by editing past entries.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default IntegrityPage;
